import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardSidebar from "./DashboardSidebar";
import DashboardHeader from "./DashboardHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useFirebase } from "@/contexts/FirebaseContext";
import { doc, getDoc, setDoc, getFirestore } from "firebase/firestore";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface DashboardLayoutProps {
  children: ReactNode;
  role: "student" | "instructor" | "admin";
}

const DashboardLayout = ({ children, role }: DashboardLayoutProps) => {
  const { user } = useAuth();
  const { currentUser } = useFirebase();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Ensure user data is synchronized between Auth and Firestore
  useEffect(() => {
    const syncUserData = async () => {
      if (user && currentUser && !isDemoMode()) {
        try {
          const db = getFirestore();
          if (!db) return;
          
          // Check if user exists in Firestore with correct ID
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) {
            console.log("Synchronizing user data to Firestore...");
            
            // Create user document with auth ID
            await setDoc(userRef, {
              id: currentUser.uid,
              email: currentUser.email,
              role: user.role || role, // Use current role or fallback to expected role
              firstName: user.firstName || "",
              lastName: user.lastName || "",
              displayName: currentUser.displayName || `${user.firstName || ""} ${user.lastName || ""}`.trim(),
              createdAt: new Date().toISOString(),
              status: "active",
              updatedAt: new Date().toISOString()
            });
            
            console.log("User data synchronized to Firestore");
          } else {
            // Check if role exists, update if needed
            const userData = userSnap.data();
            if (!userData.role) {
              console.log("Updating missing role in Firestore...");
              await setDoc(userRef, {
                role: user.role || role,
                updatedAt: new Date().toISOString()
              }, { merge: true });
              console.log("Role updated in Firestore");
            }
          }
        } catch (error) {
          console.error("Error synchronizing user data:", error);
        }
      }
    };
    
    syncUserData();
  }, [user, currentUser, role]);

  // Helper function to check if in demo mode
  const isDemoMode = () => {
    try {
      return window.location.hostname.includes('lovableproject.com') || 
             window.location.hostname.includes('gptengineer.app');
    } catch {
      return false;
    }
  };

  // Check if user is authenticated and has the correct role
  useEffect(() => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate(`/login?role=${role}`);
      return;
    }

    // If user's role doesn't match the required role, redirect to their appropriate dashboard
    if (user.role !== role) {
      const currentPath = window.location.pathname;
      const isProfileOrSettings = currentPath.includes('/profile') || currentPath.includes('/settings');
      
      // Redirect based on user's actual role
      if (user.role === 'student') {
        if (isProfileOrSettings) {
          navigate(currentPath.includes('/profile') ? "/student-dashboard/profile" : "/student-dashboard/settings");
        } else {
          navigate("/student-dashboard");
        }
      } else if (user.role === 'instructor') {
        if (isProfileOrSettings) {
          navigate(currentPath.includes('/profile') ? "/instructor-dashboard/profile" : "/instructor-dashboard/settings");
        } else {
          navigate("/instructor-dashboard");
        }
      } else if (user.role === 'admin') {
        if (isProfileOrSettings) {
          navigate(currentPath.includes('/profile') ? "/admin-dashboard/profile" : "/admin-dashboard/settings");
        } else {
          navigate("/admin-dashboard");
        }
      }
    }
  }, [user, role, navigate]);

  // If not authenticated, don't render the dashboard
  if (!user) {
    return null;
  }

  // Only allow access if user role matches the required role
  if (user.role !== role) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col md:flex-row overflow-hidden bg-background text-foreground">
      {/* Mobile Menu */}
      <div className="md:hidden">
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden fixed top-4 left-4 z-[100] bg-white shadow-md hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px] sm:w-[320px]">
            <DashboardSidebar role={role} isMobile={true} onClose={() => setIsMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <DashboardSidebar role={role} isMobile={false} />
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashboardHeader />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-background">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
